import React from "react";
import PropTypes from "prop-types";
import { startCase } from "lodash";

import { useFeatures } from "components/FeatureFlag";

import { useConditionModal, useConditionQuery } from "./hooks";
import { Button, ConditionGroup, Form, Modal } from "./components";

export function TaskConditionModal({
  workflowId,
  itemId,
  itemType,
  isCreated,
  subtitle,
  conditionsGroup,
  conditionTypes,
  disabled,
  onDone,
  onClose,
}) {
  const { formProps, submit, remove, group, isValid, isLoading } =
    useConditionModal(
      workflowId,
      itemId,
      itemType,
      isCreated,
      conditionsGroup,
      conditionTypes,
      (data) => {
        onDone(data);
        onClose();
      }
    );

  return (
    <Modal
      data-test="task-condition-modal"
      size="large"
      style={{ maxWidth: 1200 }}
      isModalOpen
      onClose={onClose}
    >
      <Modal.Header
        subtitle={subtitle}
        style={{ color: "#AAAAAA" }}
        onClose={onClose}
      >
        Configure {startCase(itemType)} Conditions
      </Modal.Header>
      <Modal.Body>
        <Form id="taskCondition" {...formProps} onSubmit={submit}>
          <div style={{ width: 67, paddingRight: 16, textAlign: "right" }}>
            <b>When</b>
          </div>
          <ConditionGroup disabled={disabled} {...group} />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <div
          style={{
            display: "flex",
            justifyContent: conditionsGroup ? "space-between" : "flex-end",
          }}
        >
          {conditionsGroup && (
            <Button
              basic
              content="Remove When"
              type="button"
              disabled={disabled || isLoading}
              onClick={() => {
                remove(conditionsGroup.id);
              }}
            />
          )}
          <Button
            color="green"
            content={conditionsGroup ? "Save" : "Add"}
            loading={isLoading}
            disabled={!isValid || disabled || isLoading}
            type="submit"
            form="taskCondition"
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
}

TaskConditionModal.propTypes = {
  workflowId: PropTypes.string.isRequired,
  conditionTypes: PropTypes.array.isRequired,
  conditionsGroup: PropTypes.object,
  itemId: PropTypes.string,
  itemType: PropTypes.oneOf(["task", "nextWorkflow"]),
  isCreated: PropTypes.bool,
  subtitle: PropTypes.string,
  disabled: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

TaskConditionModal.defaultProps = {
  conditionsGroup: null,
  itemId: null,
  itemType: "task",
  isCreated: false,
  subtitle: "",
  disabled: false,
};

function Connector({
  workflowId,
  conditionsGroup,
  order,
  onClose,
  itemType,
  ...props
}) {
  const { withStartDateCondition } = useFeatures("conditional-next-workflows");

  const {
    loadingQuery,
    conditionTypes,
    conditionsGroup: cg,
    error,
  } = useConditionQuery(
    workflowId,
    conditionsGroup,
    itemType === "task" ? order : null
  );

  const filteredConditionTypes = React.useMemo(() => {
    return conditionTypes.map((conditionType) => {
      if (conditionType.type === "This Workflow") {
        return {
          ...conditionType,
          parameters:
            withStartDateCondition || itemType === "task"
              ? conditionType.parameters
              : conditionType.parameters.filter((param) => {
                  return param.name !== "Start Date";
                }),
        };
      }

      return conditionType;
    });
  }, [conditionTypes, withStartDateCondition, itemType]);

  if (loadingQuery)
    return <Modal style={{ maxWidth: "500px" }} isModalOpen isLoading />;

  if (error) {
    return (
      <Modal size="large" style={{ maxWidth: 1200 }} isModalOpen>
        <Modal.Header onClose={onClose}>
          Configure {startCase(itemType)} Conditions
        </Modal.Header>
        <Modal.Body>{error}</Modal.Body>
      </Modal>
    );
  }

  return (
    <TaskConditionModal
      conditionTypes={filteredConditionTypes}
      conditionsGroup={cg}
      onClose={onClose}
      workflowId={workflowId}
      itemType={itemType}
      {...props}
    />
  );
}

Connector.propTypes = {
  workflowId: PropTypes.string.isRequired,
  conditionsGroup: PropTypes.object,
  order: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  itemType: PropTypes.oneOf(["task", "nextWorkflow"]),
};
Connector.defaultProps = {
  conditionsGroup: null,
  itemType: "task",
  order: 0,
};

export default Connector;
